import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Loading from '../components/common/Loading';
import {
    MdPeople,
    MdAdminPanelSettings,
    MdLocalOffer,
    MdDashboard,
    MdAccountBalanceWallet,
    MdShowChart,
    MdReplay,
    MdReceiptLong,
    MdLayers,
    MdSettings,
} from "react-icons/md";
import { FaRegCreditCard } from "react-icons/fa6";
import RequireSuperAdmin from './RequireSuperAdmin';

// Lazy load components
const ProtectedRoute = lazy(() => import('./Protectedroute'));
const Layout = lazy(() => import('../components/layout/Layout'));
const IndexLogin = lazy(() => import('../pages/Login/IndexLogin'));
const ForgotPasswordIndex = lazy(() => import('../pages/Forgotpassword/ForgetPassIndex'));
const NotFound = lazy(() => import('../components/common/NotFound'));
const Overview = lazy(() => import('../pages/Overview/Overview'));
const Dashboard = lazy(() => import('../pages/Dashboard/IndexPage'));
const ArtistBalance = lazy(() => import('../pages/ArtistBalance/ArtistBalance'));
const ArtistDetail = lazy(() => import('../pages/ArtistBalance/compoent/ArtistDetail'));
const AdminManagement = lazy(() => import('../pages/AdminManagement/AdminManagement'));
const Sales = lazy(() => import('../pages/Sales/Sales'));
const SaleDetail = lazy(() => import('../pages/Sales/SaleDetail'));
const PayoutHistory = lazy(() => import('../pages/PaymentMangement/PayoutHistory'));
const RefundRequests = lazy(() => import('../pages/RefundRequests/RefundRequests'));
const CouponManagement = lazy(() => import('../pages/Coupons/CouponManagement'));
const RefundRecords = lazy(() => import('../pages/RefundRecords/RefundRecords'));
const Plans = lazy(() => import('../pages/Plans/Plans'));
const Settings = lazy(() => import('../pages/Settings/Settings'));


// Error Boundary Component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Error caught by boundary:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    backgroundColor: '#0A0A0A',
                    color: '#fff',
                    textAlign: 'center',
                    padding: '20px'
                }}>
                    <div>
                        <h1>Something went wrong.</h1>
                        <button
                            onClick={() => window.location.href = '/'}
                            style={{
                                marginTop: '20px',
                                padding: '10px 20px',
                                backgroundColor: '#F5A623',
                                color: '#000',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: 600,
                            }}
                        >
                            Go to Home
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// Sidebar navigation, grouped as in the design. `superadminOnly` items are hidden from plain admins.
export const menuItems = [
    { text: 'Overview', icon: <MdDashboard size={24} />, path: '/overview' },
    { group: 'People', text: 'User Management', icon: <MdPeople size={24} />, path: '/dashboard' },
    { group: 'People', text: 'Artist Balance', icon: <MdAccountBalanceWallet size={24} />, path: '/artist-balance' },
    { group: 'People', text: 'Admin Management', icon: <MdAdminPanelSettings size={24} />, path: '/admin-management', superadminOnly: true },
    { group: 'Money', text: 'Sales', icon: <MdShowChart size={24} />, path: '/sales' },
    { group: 'Money', text: 'Payout Management', icon: <FaRegCreditCard size={24} />, path: '/payout-history' },
    { group: 'Money', text: 'Refund Requests', icon: <MdReplay size={24} />, path: '/refund-requests' },
    { group: 'Money', text: 'Refund Records', icon: <MdReceiptLong size={24} />, path: '/refund-records' },
    { group: 'Money', text: 'Coupon Management', icon: <MdLocalOffer size={24} />, path: '/coupon-management' },
    { group: 'Platform', text: 'Plans', icon: <MdLayers size={24} />, path: '/plans' },
    { group: 'Platform', text: 'Settings', icon: <MdSettings size={24} />, path: '/settings' },
]

// Pages rendered inside the Layout's Outlet.
const pageRoutes = [
    { path: 'overview', Component: Overview },
    { path: 'dashboard', Component: Dashboard },
    { path: 'artist-balance', Component: ArtistBalance },
    { path: 'artist-balance/:id', Component: ArtistDetail },
    { path: 'admin-management', Component: AdminManagement, superadminOnly: true },
    { path: 'sales', Component: Sales },
    { path: 'sales/:id', Component: SaleDetail },
    { path: 'payout-history', Component: PayoutHistory },
    { path: 'refund-requests', Component: RefundRequests },
    { path: 'coupon-management', Component: CouponManagement },
    { path: 'refund-records', Component: RefundRecords },
    { path: 'plans', Component: Plans },
    { path: 'settings', Component: Settings },
];

const withSuspense = (element) => <Suspense fallback={<Loading />}>{element}</Suspense>;

const AppRoutes = () => {
    return (
        <ErrorBoundary>
            <Suspense fallback={<Loading message="Loading page..." />}>
                <Routes>
                    {/* Public Routes */}
                    <Route path="/login" element={withSuspense(<IndexLogin />)} />
                    <Route path="/forgot-password" element={withSuspense(<ForgotPasswordIndex />)} />

                    {/* Protected Routes with Layout */}
                    <Route
                        path="/"
                        element={withSuspense(
                            <ProtectedRoute>
                                <Layout />
                            </ProtectedRoute>
                        )}
                    >
                        <Route index element={<Navigate to="/overview" replace />} />
                        {pageRoutes.map(({ path, Component, superadminOnly }) => (
                            <Route
                                key={path}
                                path={path}
                                element={withSuspense(
                                    superadminOnly
                                        ? <RequireSuperAdmin><Component /></RequireSuperAdmin>
                                        : <Component />
                                )}
                            />
                        ))}
                    </Route>

                    {/* 404 Not Found */}
                    <Route path="/not-found" element={withSuspense(<NotFound />)} />

                    {/* Catch all - redirect to 404 */}
                    <Route path="*" element={<Navigate to="/not-found" replace />} />
                </Routes>
            </Suspense>
        </ErrorBoundary>
    );
};

export default AppRoutes;
